import React, { useState, useEffect } from 'react';
import { previewReport, viewReport, WrapUpReport, reportToken, safeReportUrl } from '../reportAccess';
import { GlassCard } from './ui/GlassCard';
import { Button } from './ui/Button';
import { 
    Loader2, XCircle, Star, Image as ImageIcon, BarChart3, 
    Users, Printer, Eye, Camera, ExternalLink, Heart, Calendar, MapPin 
  } from 'lucide-react';


interface WrapUpPortalProps {
  quoteId: string;
  adminPreview?: boolean;
}

export const WrapUpPortal: React.FC<WrapUpPortalProps> = ({ quoteId, adminPreview = false }) => {
  const accessToken = reportToken();
  const [quote, setQuote] = useState<WrapUpReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // ⚡ REPLACE THIS with your actual Google Business Review Link
  const GOOGLE_REVIEW_LINK = "https://g.page/r/CQ7ZTjICqo2BEAI/review";

  useEffect(() => {
    let active = true;
    setQuote(null);
    setIsLoading(true);
    const fetchWrapUpData = async () => {
      try {
        const data = await (adminPreview ? previewReport(quoteId) : viewReport(quoteId));
        if (active) setQuote(data);
      } catch (err) {
        // A public failure intentionally reveals no report or client details.
      } finally {
        if (active) setIsLoading(false);
      }
    };

    if (quoteId) void fetchWrapUpData();
    else setIsLoading(false);
    return () => { active = false; };
  }, [quoteId, adminPreview, accessToken]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="text-brand-neonPink animate-spin" size={48} />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white p-4 text-center">
        <XCircle className="text-red-500 mb-4" size={64} />
        <h2 className="text-2xl font-bold mb-2">Report Not Available</h2>
        <p className="text-slate-400">{!adminPreview && !accessToken ? 'This older link needs a secure report link. Please contact QuikPix for a replacement.' : 'This report is unpublished, unavailable or its access has been revoked. Please contact QuikPix.'}</p>
      </div>
    );
  }

  // ⚡ FIX: Safely extract JSON data whether Supabase sends it as an object or a text string
  let kpis: any = {};
  const rawKpis = (quote as any).kpi_data;
  
  if (typeof rawKpis === 'string') {
    try { kpis = JSON.parse(rawKpis); } catch (e) { kpis = {}; }
  } else {
    kpis = rawKpis || {};
  }

  // Only show the KPI section if at least ONE number was actually typed in
  const hasKpis = Object.values(kpis).some(val => val !== '' && val !== null && val !== undefined);
  
  // Calculate demographic percentages safely
  const menCount = Number(kpis.men) || 0;
  const womenCount = Number(kpis.women) || 0;
  // Use the total faces they typed in, OR calculate it if they left "Total Faces" blank
  const totalFaces = Number(kpis.faces) || (menCount + womenCount) || 1; 
  const menPct = Math.round((menCount / totalFaces) * 100) || 0;
  const womenPct = Math.round((womenCount / totalFaces) * 100) || 0;

  return (
    <div className="min-h-screen bg-slate-950 py-12 px-4 sm:px-6 lg:px-8 font-sans selection:bg-brand-neonPink selection:text-slate-900">
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* HEADER SECTION */}
        <div className="text-center animate-fade-in-up">
          <div className="text-2xl font-bold text-brand-neonPink mb-6 tracking-widest uppercase">QUIKPIX</div>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4 tracking-tight">
            Event Wrap-Up
          </h1>
          <p className="text-2xl text-white font-bold mb-6">
            {quote.title}
          </p>
          
          {/* ⚡ THE FIX: Added Date and Location context */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-sm text-slate-400">
            {((quote as any).event_date || (quote as any).eventDate) && (
              <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/10">
                <Calendar size={16} className="text-brand-neonPink" />
                <span className="font-medium">
                  {new Date((quote as any).event_date || (quote as any).eventDate).toLocaleDateString('en-NZ', { weekday: 'long', month: 'long', day: 'numeric' })}
                </span>
              </div>
            )}
            
            {((quote as any).event_location || (quote as any).eventLocation) && (
              <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/10">
                <MapPin size={16} className="text-brand-neonPink" />
                <span className="font-medium">{(quote as any).event_location || (quote as any).eventLocation}</span>
              </div>
            )}
          </div>
        </div>

        {/* GALLERY & ANALYTICS LINKS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in-up delay-100">
          {safeReportUrl(quote.gallery_url) && (
            <GlassCard className="p-8 border-brand-neonPink/30 bg-brand-neonPink/5 flex flex-col items-center text-center hover:border-brand-neonPink/60 transition-colors">
              <div className="w-16 h-16 bg-brand-neonPink/20 rounded-full flex items-center justify-center mb-4 text-brand-neonPink">
                <ImageIcon size={32} />
              </div>
              <h3 className="text-2xl font-display font-bold text-white mb-2">Event Gallery</h3>
              <p className="text-slate-400 mb-6 flex-1">View, download, and share all the high-resolution captures from your event.</p>
              
              {(quote as any).gallery_password && (
                <div className="bg-slate-900/80 px-4 py-2 rounded-lg border border-white/10 mb-6">
                  <span className="text-xs text-slate-500 uppercase tracking-widest block mb-1">Password</span>
                  <span className="text-white font-mono font-bold tracking-wider">{(quote as any).gallery_password}</span>
                </div>
              )}
              
              <a href={safeReportUrl(quote.gallery_url)} target="_blank" rel="noreferrer" className="w-full">
                <Button className="w-full bg-brand-neonPink text-slate-950 font-bold hover:bg-brand-neonPink/90">
                  Open Gallery <ExternalLink size={16} className="ml-2" />
                </Button>
              </a>
            </GlassCard>
          )}

          {safeReportUrl(quote.analytics_url) && (
            <GlassCard className="p-8 border-brand-neonBlue/30 bg-brand-neonBlue/5 flex flex-col items-center text-center hover:border-brand-neonBlue/60 transition-colors">
              <div className="w-16 h-16 bg-brand-neonBlue/20 rounded-full flex items-center justify-center mb-4 text-brand-neonBlue">
                <BarChart3 size={32} />
              </div>
              <h3 className="text-2xl font-display font-bold text-white mb-2">Detailed Analytics</h3>
              <p className="text-slate-400 mb-6 flex-1">Dive deep into the engagement metrics, sharing statistics, and interaction timelines.</p>
              
              <a href={safeReportUrl(quote.analytics_url)} target="_blank" rel="noreferrer" className="w-full mt-auto">
                <Button className="w-full bg-brand-neonBlue text-slate-950 font-bold hover:bg-brand-neonBlue/90">
                  View Full Report <ExternalLink size={16} className="ml-2" />
                </Button>
              </a>
            </GlassCard>
          )}
        </div>

        {/* KPI DASHBOARD */}
        {hasKpis && (
          <GlassCard className="p-8 border-white/10 animate-fade-in-up delay-200">
            <h3 className="text-2xl font-display font-bold text-white mb-8 flex items-center gap-3">
              <BarChart3 className="text-brand-neonPink" /> Performance Snapshot
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {kpis.photos && (
                <div className="bg-slate-900/50 p-6 rounded-2xl border border-white/5 text-center">
                  <Camera className="text-brand-neonBlue mx-auto mb-3" size={24}/>
                  <p className="text-3xl font-mono font-bold text-white mb-1">{kpis.photos}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Photos Taken</p>
                </div>
              )}
              {kpis.prints && (
                <div className="bg-slate-900/50 p-6 rounded-2xl border border-white/5 text-center">
                  <Printer className="text-brand-neonBlue mx-auto mb-3" size={24}/>
                  <p className="text-3xl font-mono font-bold text-white mb-1">{kpis.prints}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Total Prints</p>
                </div>
              )}
              {kpis.reach && (
                <div className="bg-slate-900/50 p-6 rounded-2xl border border-white/5 text-center">
                  <Users className="text-brand-neonPink mx-auto mb-3" size={24}/>
                  <p className="text-3xl font-mono font-bold text-white mb-1">{kpis.reach}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Est. Reach</p>
                </div>
              )}
              {kpis.impressions && (
                <div className="bg-slate-900/50 p-6 rounded-2xl border border-white/5 text-center shadow-[0_0_20px_rgba(244,114,182,0.1)]">
                  <Eye className="text-brand-neonPink mx-auto mb-3" size={24}/>
                  <p className="text-3xl font-mono font-bold text-brand-neonPink mb-1">{kpis.impressions}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Impressions</p>
                </div>
              )}
            </div>

            {/* DEMOGRAPHICS VISUAL BAR */}
            {(kpis.men || kpis.women) && (
              <div className="bg-slate-900/50 p-6 rounded-2xl border border-white/5">
                <div className="flex justify-between items-end mb-4">
                  <div>
                    <p className="text-sm text-white font-bold mb-1">Demographics Breakdown</p>
                    <p className="text-xs text-slate-500">{kpis.faces || totalFaces} Total Faces Detected</p>
                  </div>
                </div>
                
                {/* Visual Bar */}
                <div className="h-4 w-full bg-slate-800 rounded-full overflow-hidden flex mb-3">
                  <div style={{ width: `${womenPct}%` }} className="h-full bg-brand-neonPink transition-all duration-1000 ease-out"></div>
                  <div style={{ width: `${menPct}%` }} className="h-full bg-brand-neonBlue transition-all duration-1000 ease-out"></div>
                </div>
                
                <div className="flex justify-between text-xs font-bold">
                  <span className="text-brand-neonPink">{womenPct}% Women ({kpis.women})</span>
                  <span className="text-brand-neonBlue">{menPct}% Men ({kpis.men})</span>
                </div>
              </div>
            )}
          </GlassCard>
        )}

        {/* THE GOOGLE REVIEW ASK */}
        <GlassCard className="p-8 border-yellow-500/30 bg-gradient-to-br from-yellow-500/10 to-transparent text-center animate-fade-in-up delay-300">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="text-yellow-400 fill-yellow-400 drop-shadow-[0_0_10px_rgba(250,204,21,0.5)]" size={32} />
            ))}
          </div>
          <h3 className="text-3xl font-display font-bold text-white mb-4">We loved working with you!</h3>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto text-lg">
            If you were thrilled with the experience, it would mean the world to our team if you took 60 seconds to share a quick review on Google. It's how we grow!
          </p>
          <a href={GOOGLE_REVIEW_LINK} target="_blank" rel="noreferrer">
            <Button size="lg" className="bg-yellow-500 text-slate-950 font-bold hover:bg-yellow-400 shadow-[0_0_20px_rgba(250,204,21,0.3)] transition-all hover:scale-105">
              <Heart size={20} className="mr-2" /> Share Your Experience
            </Button>
          </a>
        </GlassCard>

      </div>
    </div>
  );
};